import React from "react";
import ReportTable from "../ReportTable";
import reportTableStyles from "../ReportTable.module.css";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import useReportingYearLabels from "./useReportingYearLabels";

const Principle9 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(9);
  const { currentYear, previousYear } = useReportingYearLabels();
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};

  const complaintCategories = [
    ["Data privacy", "dataPrivacy"],
    ["Advertising", "advertising"],
    ["Cyber-security", "cyberSecurity"],
    ["Delivery of essential services", "deliveryEssentialServices"],
    ["Restrictive Trade Practices", "restrictiveTradePractices"],
    ["Unfair Trade Practices", "unfairTradePractices"],
    ["Other", "other"],
  ];

  /** Single merged grid for Q3: current + previous FY side by side (BRSR layout). */
  const complaintRowsMerged = complaintCategories.map(([category, key], idx) => ({
    _id: `p9-compl-${idx}`,
    category,
    curReceived: safeText(
      essential?.consumerComplaints?.currentFinancialYear?.[key]?.received,
      "N/A",
    ),
    curPending: safeText(
      essential?.consumerComplaints?.currentFinancialYear?.[key]?.pendings,
      "N/A",
    ),
    curRemarks: safeText(
      essential?.consumerComplaints?.currentFinancialYear?.[key]?.remark,
      "N/A",
    ),
    prevReceived: safeText(
      essential?.consumerComplaints?.previousFinancialYear?.[key]?.received,
      "N/A",
    ),
    prevPending: safeText(
      essential?.consumerComplaints?.previousFinancialYear?.[key]?.pendings,
      "N/A",
    ),
    prevRemarks: safeText(
      essential?.consumerComplaints?.previousFinancialYear?.[key]?.remark,
      "N/A",
    ),
  }));

  const productRecallRows = [
    ["Voluntary recalls", "voluntaryRecalls"],
    ["Forced recalls", "forcedRecalls"],
  ].map(([authority, key], idx) => ({
    _id: `p9-recall-${idx}`,
    authority,
    number: safeText(essential?.productRecalls?.[key]?.number, "N/A"),
    reason: safeText(essential?.productRecalls?.[key]?.reasonForRecall, "N/A"),
  }));

  const channelsRows = (
    Array.isArray(leadership?.channelsForProductInformation)
      ? leadership.channelsForProductInformation
      : []
  ).map((row, idx) => ({
    _id: row?._id || `p9-l1-${idx}`,
    channel: safeText(row?.chennel, "N/A"),
    link: safeText(row?.link, "N/A"),
  }));

  return (
    <div className={styles.principleBlock}>
      <div className={styles.principleHeading}>{`PRINCIPLE 9: ${principleTitles[8]}`}</div>
      {loading ? <div className={styles.meta}>Loading principle 9...</div> : null}

      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>

      <div className={styles.question}>
        1. Describe the mechanisms in place to receive and respond to consumer complaints and
        feedback.
      </div>
      <div className={styles.answer}>{safeText(essential?.consumerComplaintsMechanisms, "N/A")}</div>

      <div className={styles.question}>
        2. Turnover of products and/ services as a percentage of turnover from all products/service
        that carry information about:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "percentage", label: "As a percentage to total turnover" },
        ]}
        rows={[
          {
            _id: "p9-t-1",
            category: "Environmental and social parameters relevant to the product",
            percentage: safeText(
              essential?.turnoverPercentageWithInformation?.environmentalAndSocialParameters,
              "N/A",
            ),
          },
          {
            _id: "p9-t-2",
            category: "Safe and responsible usage",
            percentage: safeText(
              essential?.turnoverPercentageWithInformation?.safeAndResponsibleUsage,
              "N/A",
            ),
          },
          {
            _id: "p9-t-3",
            category: "Recycling and/or safe disposal",
            percentage: safeText(
              essential?.turnoverPercentageWithInformation?.recyclingAndSafeDisposal,
              "N/A",
            ),
          },
        ]}
      />

      <div className={styles.question}>3. Number of consumer complaints in respect of the following:</div>
      <div className={reportTableStyles.wrapper}>
        <div className={reportTableStyles.tableContainer}>
          <table className={reportTableStyles.table}>
            <thead>
              <tr className={reportTableStyles.headerRow}>
                <th
                  rowSpan={2}
                  className={reportTableStyles.headerCell}
                  style={{ width: "18%", verticalAlign: "middle" }}
                >
                  Category
                </th>
                <th
                  colSpan={2}
                  className={reportTableStyles.headerCell}
                  style={{ textAlign: "center" }}
                >
                  {currentYear
                    ? `FY ${currentYear} (Current Financial Year)`
                    : "FY ____ (Current Financial Year)"}
                </th>
                <th
                  rowSpan={2}
                  className={reportTableStyles.headerCell}
                  style={{ width: "14%", verticalAlign: "middle" }}
                >
                  Remarks
                </th>
                <th
                  colSpan={2}
                  className={reportTableStyles.headerCell}
                  style={{ textAlign: "center" }}
                >
                  {previousYear
                    ? `FY ${previousYear} (Previous Financial Year)`
                    : "FY ____ (Previous Financial Year)"}
                </th>
                <th
                  rowSpan={2}
                  className={reportTableStyles.headerCell}
                  style={{ width: "14%", verticalAlign: "middle" }}
                >
                  Remarks
                </th>
              </tr>
              <tr className={reportTableStyles.headerRow}>
                <th className={reportTableStyles.headerCell}>
                  Received during the year
                </th>
                <th className={reportTableStyles.headerCell}>
                  Pending resolution at end of year
                </th>
                <th className={reportTableStyles.headerCell}>
                  Received during the year
                </th>
                <th className={reportTableStyles.headerCell}>
                  Pending resolution at end of year
                </th>
              </tr>
            </thead>
            <tbody>
              {complaintRowsMerged.length ? (
                complaintRowsMerged.map((row) => (
                  <tr key={row._id} className={reportTableStyles.bodyRow}>
                    <td className={reportTableStyles.cell}>{row.category}</td>
                    <td className={reportTableStyles.cell}>{row.curReceived}</td>
                    <td className={reportTableStyles.cell}>{row.curPending}</td>
                    <td className={reportTableStyles.cell}>{row.curRemarks}</td>
                    <td className={reportTableStyles.cell}>{row.prevReceived}</td>
                    <td className={reportTableStyles.cell}>{row.prevPending}</td>
                    <td className={reportTableStyles.cell}>{row.prevRemarks}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className={reportTableStyles.emptyCell}
                    colSpan={7}
                  >
                    No information provided.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.question}>4. Details of instances of product recalls on account of safety issues:</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "authority", label: "Name of authority" },
          { field: "number", label: "Number" },
          { field: "reason", label: "Reason For Recall" },
        ]}
        rows={productRecallRows}
      />

      <div className={styles.question}>
        5. Does the entity have a framework/ policy on cyber security and risks related to data privacy? (Yes/No) If available, provide a web-link of the policy.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.cyberSecurityAndDataPrivacyPolicy?.action)}
        {essential?.cyberSecurityAndDataPrivacyPolicy?.action
          ? ` (${safeText(essential?.cyberSecurityAndDataPrivacyPolicy?.webLink, "N/A")})`
          : ""}
      </div>

      <div className={styles.question}>
        6. Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.
      </div>
      <div className={styles.answer}>{safeText(essential?.correctiveActions, "N/A")}</div>

      <div className={styles.question}>7. Provide the following information relating to data breaches:</div>
      <div className={`${styles.answer} ${styles.answerSubParts}`}>
        <div className={styles.answerSubPartsLine}>
          (i) Number of instances:{" "}
          {safeText(essential?.dataBreachDetails?.instancesOfDataBreaches, "N/A")}
        </div>
        <div className={styles.answerSubPartsLine}>
          (ii) % involving personal data:{" "}
          {safeText(
            essential?.dataBreachDetails?.percentageInvolvingPersonalData,
            "N/A",
          )}
        </div>
        <div className={styles.answerSubPartsLine}>
          (iii) Impact:{" "}
          {safeText(essential?.dataBreachDetails?.impactOfDataBreaches, "N/A")}
        </div>
      </div>

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>

      <div className={styles.question}>
        1. Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).
      </div>
      <ReportTable
        showSerialNumber={true}
        columns={[
          { field: "channel", label: "Channel/Platform Name" },
          { field: "link", label: "Link (If Any)" },
        ]}
        rows={channelsRows}
      />

      <div className={styles.question}>
        2. Steps taken to inform and educate consumers about safe and responsible usage of products and/or services.
      </div>
      <div className={styles.answer}>{safeText(leadership?.consumerEducation, "N/A")}</div>

      <div className={styles.question}>
        3. Mechanisms in place to inform consumers of any risk of disruption /
        discontinuation of
        essential services.
      </div>
      <div className={styles.answer}>{safeText(leadership?.riskDisclosureMechanisms, "N/A")}</div>

      <div className={styles.question}>
        4. Does the entity display product information on the product over and above what is mandated as per local laws? (Yes / No / Not Applicable)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.productInformationDisplayed?.action)}
        {leadership?.productInformationDisplayed?.action === "true"
          ? `, ${safeText(leadership?.productInformationDisplayed?.remark, "N/A")}`
          : ""}
      </div>

      <div className={styles.question}>
        5. Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole? (Yes/No)
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.consumerSatisfactionSurvey?.action)}
        {leadership?.consumerSatisfactionSurvey?.action
          ? `, ${safeText(leadership?.consumerSatisfactionSurvey?.remark, "N/A")}`
          : ""}
      </div>

      
    </div>
  );
};

export default Principle9;
